import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ImagePlus, Loader2, ScanText, X } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { api, errMsg, newRequestId } from "@/lib/api";

const TYPES = ["image/png", "image/jpeg", "image/webp"];

const Thumb = ({ file, index, total, onMove, onRemove, disabled }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div className="relative shrink-0 w-24" data-testid={`screenshot-thumb-${index}`}>
      {url && <img src={url} alt={`Screenshot ${index + 1}`} className="h-40 w-24 object-cover rounded-xl border border-border" />}
      <span className="absolute top-1 left-1 h-6 w-6 rounded-full bg-black/70 text-white text-xs flex items-center justify-center font-semibold">{index + 1}</span>
      <button disabled={disabled} data-testid={`screenshot-remove-${index}`} onClick={() => onRemove(index)} aria-label="Remove" className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-rose-600"><X className="h-3.5 w-3.5" /></button>
      <div className="flex justify-between mt-1">
        <button disabled={disabled || index === 0} data-testid={`screenshot-move-left-${index}`} onClick={() => onMove(index, -1)} aria-label="Move earlier" className="h-7 w-7 rounded-lg border border-border flex items-center justify-center disabled:opacity-30"><ArrowLeft className="h-3.5 w-3.5" /></button>
        <button disabled={disabled || index === total - 1} data-testid={`screenshot-move-right-${index}`} onClick={() => onMove(index, 1)} aria-label="Move later" className="h-7 w-7 rounded-lg border border-border flex items-center justify-center disabled:opacity-30"><ArrowRight className="h-3.5 w-3.5" /></button>
      </div>
    </div>
  );
};

export const ScreenshotUploader = ({ files, setFiles, onExtracted, config }) => {
  const inputRef = useRef(null);
  const ctrlRef = useRef(null);
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const busy = phase !== "idle";
  const maxMb = config?.max_image_mb || 10;
  const maxFiles = config?.max_images || 6;

  const add = (list) => {
    setError("");
    const incoming = Array.from(list || []);
    const bad = incoming.find((f) => !TYPES.includes(f.type));
    if (bad) return setError(`"${bad.name}" isn't supported. Use PNG, JPEG or WebP.`);
    const big = incoming.find((f) => f.size > maxMb * 1024 * 1024);
    if (big) return setError(`"${big.name}" is larger than ${maxMb} MB.`);
    const next = [...files, ...incoming].slice(0, maxFiles);
    if (files.length + incoming.length > maxFiles) toast.warning(`Up to ${maxFiles} screenshots per conversation.`);
    setFiles(next);
  };
  const move = (i, d) => { const n = [...files]; [n[i], n[i + d]] = [n[i + d], n[i]]; setFiles(n); };
  const remove = (i) => setFiles(files.filter((_, j) => j !== i));

  const extract = async () => {
    setError("");
    setPhase("uploading");
    setProgress(0);
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    form.append("request_id", newRequestId());
    try {
      const { data } = await api.post("/analyze-screenshot", form, {
        signal: ctrl.signal,
        timeout: 90000,
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 100;
          setProgress(pct);
          if (pct >= 100) setPhase("reading");
        },
      });
      onExtracted(data);
      toast.success(`Found ${data.messages.length} messages — review them below.`);
    } catch (e) {
      const msg = errMsg(e, "We couldn't read that screenshot.");
      if (msg !== "Cancelled.") setError(msg);
    } finally {
      setPhase("idle");
    }
  };

  return (
    <div className="space-y-4">
      <div
        role="button"
        tabIndex={0}
        data-testid="screenshot-dropzone"
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (!busy) add(e.dataTransfer.files); }}
        className="rounded-2xl border-2 border-dashed border-border hover:border-primary/60 bg-secondary/30 p-6 text-center cursor-pointer transition-colors"
      >
        <ImagePlus className="h-7 w-7 mx-auto text-primary mb-2" />
        <div className="text-sm font-medium">Tap to add screenshots or drop them here</div>
        <div className="text-xs text-muted-foreground mt-1">PNG, JPEG or WebP · up to {maxFiles} images · {maxMb} MB each · put them oldest first</div>
        <input ref={inputRef} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="screenshot-dropzone-input"
          onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </div>

      {files.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-1" data-testid="screenshot-thumbs">
          {files.map((f, i) => (
            <Thumb key={`${f.name}-${f.lastModified}-${i}`} file={f} index={i} total={files.length} onMove={move} onRemove={remove} disabled={busy} />
          ))}
        </div>
      )}

      {busy && (
        <div className="space-y-2" data-testid="ocr-progress">
          <div className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin text-primary" />{phase === "uploading" ? `Uploading… ${progress}%` : "Reading messages from your screenshots…"}</div>
          <Progress value={phase === "uploading" ? progress * 0.4 : 70} className="h-1.5" />
        </div>
      )}
      {error && <div data-testid="ocr-error" className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-sm p-3">{error}</div>}

      <div className="flex gap-2">
        <button data-testid="extract-text-button" disabled={!files.length || busy} onClick={extract}
          className="flex-1 h-11 rounded-full brand-gradient text-white font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40 hover:opacity-90 transition-opacity">
          <ScanText className="h-4 w-4" /> Extract conversation
        </button>
        {busy && (
          <button data-testid="cancel-extract-button" onClick={() => ctrlRef.current?.abort()} className="h-11 px-5 rounded-full border border-border text-sm font-medium">Cancel</button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Images are processed in memory to read the text and are not saved.</p>
    </div>
  );
};
